import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ExternalLink, Eye, Heart, Image as ImageIcon, MessageSquare, Play, Rocket, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatNumberCompact } from '@/lib/format';
import { imgProxy } from '@/lib/img';
import { cn } from '@/lib/utils';

export interface PostPreviewData {
  id: string;
  caption?: string | null;
  permalink?: string | null;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  /** R2 keys (preferred if present — permanent, plays inline). */
  r2ThumbKey?: string | null;
  r2MediaKey?: string | null;
  mediaType?: string | null;
  postedAt?: string | null;
  likes?: number | null;
  comments?: number | null;
  views?: number | null;
  reach?: number | null;
  isBoosted?: boolean | null;
  /** Username / brand label shown above title. */
  source?: string;
}

interface Props {
  post: PostPreviewData | null;
  onClose: () => void;
}

export function PostPreview({ post, onClose }: Props) {
  const open = post !== null;
  const isVideo = post?.mediaType === 'VIDEO';
  const hasPermalink = Boolean(post?.permalink);
  // If we've mirrored the .mp4 to R2, play it inline via <video>. Fall back
  // to IG's embed widget (poster-only, taps to IG) when only the live IG
  // CDN URL is available — its data-center 403s prevent direct playback.
  const hasMirroredVideo = isVideo && Boolean(post?.r2MediaKey);
  const useEmbed = isVideo && !hasMirroredVideo && hasPermalink;
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-border bg-popover shadow-2xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            // IG embed widget is ~540px wide and handles its own layout
            useEmbed
              ? 'w-[560px] max-w-[95vw] max-h-[92vh] overflow-y-auto'
              : isVideo
                ? 'w-[420px] max-w-[95vw]'
                : 'w-[95vw] max-w-3xl',
          )}
        >
          <Dialog.Title className="sr-only">Post preview</Dialog.Title>
          {post && (useEmbed ? <EmbedInner post={post} onClose={onClose} /> : <PostInner post={post} onClose={onClose} />)}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Load Instagram's embed.js script once and reprocess on mount so the
// <blockquote class="instagram-media"> renders as a fully playable reel.
let igEmbedScriptLoading: Promise<void> | null = null;
function loadIgEmbedScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w.instgrm?.Embeds) return Promise.resolve();
  if (igEmbedScriptLoading) return igEmbedScriptLoading;
  igEmbedScriptLoading = new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>('script[src*="instagram.com/embed.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      if (w.instgrm?.Embeds) resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://www.instagram.com/embed.js';
    s.async = true;
    s.onload = () => resolve();
    document.body.appendChild(s);
  });
  return igEmbedScriptLoading;
}

function EmbedInner({ post, onClose }: { post: PostPreviewData; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    loadIgEmbedScript().then(() => {
      if (cancelled) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      if (w.instgrm?.Embeds?.process) {
        w.instgrm.Embeds.process();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [post.permalink]);

  return (
    <div className="relative bg-popover">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 z-10 rounded-full bg-black/60 p-1.5 text-white backdrop-blur-sm hover:bg-black/80"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      <div ref={ref} className="flex justify-center p-2">
        <blockquote
          className="instagram-media"
          data-instgrm-permalink={post.permalink ?? ''}
          data-instgrm-version="14"
          style={{
            background: '#FFF',
            border: 0,
            margin: 0,
            maxWidth: 540,
            minWidth: 326,
            padding: 0,
            width: '100%',
          }}
        >
          <a href={post.permalink ?? '#'} target="_blank" rel="noreferrer">
            View on Instagram
          </a>
        </blockquote>
      </div>
    </div>
  );
}

function PostInner({ post, onClose }: { post: PostPreviewData; onClose: () => void }) {
  const isVideo = post.mediaType === 'VIDEO';
  const [videoFailed, setVideoFailed] = useState(false);
  // For videos: prefer image_url (the .mp4) for the <video> tag; fall back
  // to thumbnail_url. For non-videos: prefer image_url (the photo) over
  // thumbnail.
  // Prefer the R2-mirrored media (permanent, plays inline); fall back to
  // the live IG CDN .mp4 URL (often 403s from data centers, hence the
  // error state).
  const videoSrcResolved = isVideo
    ? imgProxy(post.imageUrl?.match(/\.mp4/i) ? post.imageUrl : null, post.r2MediaKey)
    : undefined;
  const imgSrcResolved = !isVideo
    ? imgProxy(post.imageUrl || post.thumbnailUrl, post.r2MediaKey ?? post.r2ThumbKey)
    : imgProxy(post.thumbnailUrl, post.r2ThumbKey);
  const caption = post.caption ?? '';
  const date = post.postedAt ? new Date(post.postedAt).toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) : null;

  return (
    <div
      className={cn(
        'flex max-h-[92vh] flex-col',
        // Reels: vertical stack always (mobile-like). Images: side-by-side on
        // wide screens so the caption is readable next to the photo.
        !isVideo && 'md:flex-row',
      )}
    >
      {/* Media */}
      <div
        className={cn(
          'relative flex shrink-0 items-center justify-center bg-black',
          isVideo
            ? 'aspect-[9/16] max-h-[70vh] w-full'
            : 'min-h-[18rem] md:h-auto md:w-1/2',
        )}
      >
        {videoSrcResolved && !videoFailed ? (
          <video
            src={videoSrcResolved}
            poster={imgSrcResolved}
            controls
            autoPlay
            playsInline
            loop
            onError={() => setVideoFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : videoSrcResolved && videoFailed ? (
          <div className="relative h-full w-full">
            {imgSrcResolved && (
              <img
                src={imgSrcResolved}
                alt=""
                className="h-full w-full object-cover opacity-60"
              />
            )}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/40 px-6 text-center">
              <Play className="h-12 w-12 text-white" />
              <span className="text-xs text-white/90">
                Video can't play here — Instagram's CDN doesn't allow embedding.
              </span>
              {post.permalink && (
                <a
                  href={post.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-xs font-medium text-black hover:bg-white/90"
                >
                  Watch on Instagram <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        ) : imgSrcResolved ? (
          <img
            src={imgSrcResolved}
            alt=""
            className="max-h-[80vh] max-w-full object-contain"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center">
            {isVideo ? (
              <Play className="h-12 w-12 text-white/40" />
            ) : (
              <ImageIcon className="h-12 w-12 text-white/40" />
            )}
            <span className="text-2xs text-white/60">
              {isVideo
                ? 'Preview unavailable — open on Instagram to watch'
                : 'Preview unavailable — open on Instagram'}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 text-white backdrop-blur-sm hover:bg-black/70 md:hidden"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Meta */}
      <div
        className={cn(
          'flex min-h-0 flex-col',
          !isVideo && 'md:w-1/2',
        )}
      >
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {post.source && (
              <span className="truncate font-semibold text-sm">{post.source}</span>
            )}
            {date && <span className="text-2xs text-muted-foreground">· {date}</span>}
            {post.mediaType && (
              <Badge variant="muted" className="text-2xs">
                {post.mediaType === 'VIDEO'
                  ? 'Reel'
                  : post.mediaType === 'CAROUSEL_ALBUM'
                    ? 'Carousel'
                    : 'Post'}
              </Badge>
            )}
            {post.isBoosted && (
              <Badge
                variant="muted"
                className="bg-sky-500/15 text-2xs text-sky-600 dark:text-sky-400"
                title="Currently boosted with paid spend"
              >
                <Rocket className="mr-1 h-3 w-3" />
                Boosted
              </Badge>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="hidden text-muted-foreground hover:text-foreground md:block"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {caption ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{caption}</p>
          ) : (
            <p className="text-sm italic text-muted-foreground">No caption</p>
          )}
        </div>

        <div className="border-t border-border/60 px-4 py-3">
          <div className="flex flex-wrap gap-3 text-xs">
            {post.likes != null && (
              <Stat icon={<Heart className="h-3 w-3" />} value={post.likes} label="likes" />
            )}
            {post.comments != null && (
              <Stat icon={<MessageSquare className="h-3 w-3" />} value={post.comments} label="comments" />
            )}
            {post.views != null && post.views > 0 && (
              <Stat icon={<Eye className="h-3 w-3" />} value={post.views} label="views" />
            )}
            {post.reach != null && post.reach > 0 && (
              <Stat icon={<Eye className="h-3 w-3" />} value={post.reach} label="reach" />
            )}
          </div>
          {post.permalink && (
            <a
              href={post.permalink}
              target="_blank"
              rel="noreferrer"
              className={cn(
                'mt-3 inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs font-medium',
                'hover:bg-accent',
              )}
            >
              View on Instagram <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      {icon}
      <span className="font-mono tabular-nums text-foreground">{formatNumberCompact(value)}</span>
      <span>{label}</span>
    </span>
  );
}
